import type { DeckCard } from '../../shared/types';
import { banStatus, BAN_LIMIT } from './banlist';

export type ZoneStatus = 'ok' | 'warn' | 'bad';
export interface Legality {
  status: 'Legal' | 'Draft' | 'Illegal';
  issues: string[];              // hard violations (make the deck illegal)
  drafts: string[];              // incomplete notes (make the deck a draft)
  problemCards: Set<string>;     // cards currently causing a violation (marked red)
  zone: Record<'skill' | 'main' | 'extra' | 'side' | 'considering', ZoneStatus>;
}

interface Zones { main: DeckCard[]; extra: DeckCard[]; side: DeckCard[]; considering: DeckCard[]; }
type Meta = { category: string; types: string[] | null } | undefined;

const sum = (list: DeckCard[]) => list.reduce((s, c) => s + c.count, 0);

/**
 * Speed Duel deck legality. Rules:
 *  - ≤3 copies of any card across Main+Extra+Side (Considering is exempt).
 *  - Banlist "point pools" across Main+Extra+Side+Skill (Considering exempt):
 *      Forbidden = 0; Limit 1 list ≤1 total; Limit 2 list ≤2; Limit 3 list ≤3.
 *  - Zone sizes: Main 20–40, Extra ≤6, Side ≤15; a Skill is required.
 *  - Zone types: Skill only in Skill, Fusion only in Extra, others in Main/Side.
 * Nothing here blocks building/saving — it only classifies the deck.
 */
export function computeLegality(skill: string | null, zones: Zones, meta: (name: string) => Meta): Legality {
  const issues: string[] = [];
  const drafts: string[] = [];
  const problem = new Set<string>();
  const zone: Legality['zone'] = { skill: 'ok', main: 'ok', extra: 'ok', side: 'ok', considering: 'ok' };

  // ── ≤3 copies per card across Main+Extra+Side ──
  const copies = new Map<string, number>();
  for (const c of [...zones.main, ...zones.extra, ...zones.side]) copies.set(c.name, (copies.get(c.name) ?? 0) + c.count);
  for (const [n, c] of copies) if (c > 3) { issues.push(`${c}× "${n}" — max 3 copies per card.`); problem.add(n); }

  // ── Banlist point pools across Main+Extra+Side+Skill ──
  const banAgg = new Map<string, number>();
  for (const c of [...zones.main, ...zones.extra, ...zones.side, ...(skill ? [{ name: skill, count: 1 }] : [])]) {
    banAgg.set(c.name, (banAgg.get(c.name) ?? 0) + c.count);
  }
  const totals: Record<'limit1' | 'limit2' | 'limit3', number> = { limit1: 0, limit2: 0, limit3: 0 };
  const listCards: Record<'limit1' | 'limit2' | 'limit3', string[]> = { limit1: [], limit2: [], limit3: [] };
  const forbidden: string[] = [];
  for (const [n, c] of banAgg) {
    const st = banStatus(n);
    if (st === 'forbidden') forbidden.push(n);
    else if (st) { totals[st] += c; listCards[st].push(n); }
  }
  if (forbidden.length) { issues.push(`Forbidden card${forbidden.length > 1 ? 's' : ''}: ${forbidden.map(n => `"${n}"`).join(', ')}.`); forbidden.forEach(n => problem.add(n)); }
  (['limit1', 'limit2', 'limit3'] as const).forEach(lk => {
    const lim = BAN_LIMIT[lk];
    if (totals[lk] > lim) {
      issues.push(`${totals[lk]} cards from the Limit ${lim} list — max ${lim} allowed.`);
      listCards[lk].forEach(n => problem.add(n));
    }
  });

  // ── Zone sizes ──
  const m = sum(zones.main), ex = sum(zones.extra), sd = sum(zones.side);
  if (m > 30) { issues.push(`Main Deck has ${m} cards — max 30.`); zone.main = 'bad'; }
  else if (m < 20) { drafts.push(`Main Deck has ${m} cards — need at least 20.`); zone.main = 'warn'; }
  if (ex > 6) { issues.push(`Extra Deck has ${ex} cards — max 6.`); zone.extra = 'bad'; }
  if (sd > 6) { issues.push(`Side Deck has ${sd} cards — max 6.`); zone.side = 'bad'; }
  if (!skill) { drafts.push('No Skill card selected.'); zone.skill = 'warn'; }

  // ── Zone-type legality (safety for loaded decks) ──
  const isSkill = (mt: NonNullable<Meta>) => mt.category === 'Skill';
  const isFusion = (mt: NonNullable<Meta>) => mt.category === 'Monster' && !!mt.types?.includes('Fusion');
  const badType = (name: string, ok: (mt: NonNullable<Meta>) => boolean) => { const mt = meta(name); return mt ? !ok(mt) : false; };
  if (skill && badType(skill, isSkill)) { issues.push(`"${skill}" is not a Skill card.`); problem.add(skill); }
  for (const c of zones.main) if (badType(c.name, mt => !isSkill(mt) && !isFusion(mt))) { issues.push(`"${c.name}" can't be in the Main Deck.`); problem.add(c.name); }
  for (const c of zones.extra) if (badType(c.name, isFusion)) { issues.push(`"${c.name}" is not a Fusion Monster (Extra Deck).`); problem.add(c.name); }
  for (const c of zones.side) if (badType(c.name, mt => !isSkill(mt) && !isFusion(mt))) { issues.push(`"${c.name}" can't be in the Side Deck.`); problem.add(c.name); }

  // Mark any zone containing a problem card red.
  if (skill && problem.has(skill)) zone.skill = 'bad';
  if (zones.main.some(c => problem.has(c.name))) zone.main = 'bad';
  if (zones.extra.some(c => problem.has(c.name))) zone.extra = 'bad';
  if (zones.side.some(c => problem.has(c.name))) zone.side = 'bad';

  const status = issues.length ? 'Illegal' : drafts.length ? 'Draft' : 'Legal';
  return { status, issues, drafts, problemCards: problem, zone };
}
